import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Globe2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    nationality: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data.countries));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to VisAura!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-grid flex items-center justify-center px-4 py-12">
      <div className="blob right-[10%] top-[20%] h-[300px] w-[300px] rounded-full bg-indigo-400" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 shadow-lg shadow-teal-500/30">
            <Globe2 className="h-5 w-5 text-white" />
          </div>
          <span className="font-heading text-2xl font-bold">VisAura</span>
        </Link>

        <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-2xl shadow-slate-900/5">
          <h1 className="font-heading text-2xl font-bold">Create your account</h1>
          <p className="mt-1 text-sm text-slate-500">Save trips, get premium reports, and more.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="full_name">Full name</Label>
              <Input
                id="full_name"
                required
                data-testid="register-name"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="register-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password">Password (min 6 chars)</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                data-testid="register-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label>Nationality</Label>
              <Select
                value={form.nationality}
                onValueChange={(v) => setForm({ ...form, nationality: v })}
              >
                <SelectTrigger data-testid="register-nationality" className="mt-1.5 h-11">
                  <SelectValue placeholder="Select your passport country" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {countries.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="submit"
              data-testid="register-submit"
              disabled={loading}
              className="h-11 w-full bg-slate-900 text-base font-semibold hover:bg-slate-800"
            >
              {loading ? "Creating..." : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-teal-600 hover:text-teal-700">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
